import { TFunction } from 'i18next';

/**
 * Maps backend error responses to translated error messages
 * Following security best practices by not exposing system internals
 */
export const getAuthErrorMessage = (
  error: any,
  t: TFunction,
  context: 'signIn' | 'signUp' | 'passwordReset' | 'passwordResetConfirm' | 'emailVerification' | 'phoneVerification'
): string => {
  // Handle network errors
  if (!error.response) {
    if (error.request) {
      // Request was made but no response received (network error)
      return t(`auth:${context}.networkError`);
    }
    // Something else happened
    return t(`auth:${context}.serverError`);
  }

  const status = error.response?.status;
  const detail = error.response?.data?.detail || '';
  const message = error.response?.data?.message || '';
  const errorText = detail || message;

  // Map specific backend error messages to translation keys
  switch (context) {
    case 'signIn':
      return mapSignInError(status, errorText, t);

    case 'signUp':
      return mapSignUpError(status, errorText, t);

    case 'passwordReset':
      return mapPasswordResetError(status, errorText, t);

    case 'passwordResetConfirm':
      return mapPasswordResetConfirmError(status, errorText, t);

    case 'emailVerification':
      return mapEmailVerificationError(status, errorText, t);

    case 'phoneVerification':
      return mapPhoneVerificationError(status, errorText, t);

    default:
      return getGenericError(status, t, context);
  }
};

function mapSignInError(status: number, errorText: string, t: TFunction): string {
  if (status === 401) {
    if (errorText.toLowerCase().includes('incorrect') || errorText.toLowerCase().includes('password')) {
      return t('auth:signIn.invalidCredentials');
    }
  }

  if (status === 403) {
    if (errorText.toLowerCase().includes('verify') || errorText.toLowerCase().includes('email')) {
      return t('auth:signIn.verifyEmailFirst');
    }
    if (errorText.toLowerCase().includes('inactive')) {
      return t('auth:signIn.accountInactive');
    }
    return t('auth:signIn.accountDenied');
  }

  return getGenericError(status, t, 'signIn');
}

function mapSignUpError(status: number, errorText: string, t: TFunction): string {
  if (status === 400) {
    // Check for the generic "already exist" message (for user enumeration prevention)
    if (errorText.toLowerCase().includes('already exist') || errorText.toLowerCase().includes('already registered')) {
      // Use the generic message that doesn't reveal which field is duplicate
      return t('auth:signUp.emailAlreadyRegistered');
    }
  }

  if (status === 500) {
    if (errorText.toLowerCase().includes('verification email') || errorText.toLowerCase().includes('send')) {
      return t('auth:signUp.verificationEmailFailed');
    }
  }

  if (status === 422) {
    // Validation errors - return the detail if available, otherwise generic
    return errorText || t('auth:signUp.registrationFailed');
  }

  return getGenericError(status, t, 'signUp');
}

function mapPasswordResetError(status: number, errorText: string, t: TFunction): string {
  if (status === 400) {
    return t('auth:passwordReset.emailInvalid');
  }

  if (status === 500) {
    return t('auth:passwordReset.sendFailed');
  }

  return getGenericError(status, t, 'passwordReset');
}

function mapPasswordResetConfirmError(status: number, errorText: string, t: TFunction): string {
  if (status === 400) {
    if (errorText.toLowerCase().includes('expired')) {
      return t('auth:passwordResetConfirm.tokenExpired');
    }
    if (errorText.toLowerCase().includes('invalid') || errorText.toLowerCase().includes('token')) {
      return t('auth:passwordResetConfirm.invalidResetLink');
    }
  }

  if (status === 404) {
    if (errorText.toLowerCase().includes('user not found')) {
      return t('auth:passwordResetConfirm.userNotFound');
    }
  }

  return getGenericError(status, t, 'passwordResetConfirm');
}

function mapEmailVerificationError(status: number, errorText: string, t: TFunction): string {
  if (status === 400) {
    if (errorText.toLowerCase().includes('expired')) {
      return t('auth:emailVerification.tokenExpired');
    }
    if (errorText.toLowerCase().includes('invalid')) {
      return t('auth:emailVerification.invalidToken');
    }
    if (errorText.toLowerCase().includes('already verified')) {
      return t('auth:emailVerification.emailAlreadyVerified');
    }
  }

  if (status === 404) {
    if (errorText.toLowerCase().includes('user')) {
      return t('auth:emailVerification.userNotFound');
    }
    return t('auth:emailVerification.emailNotFound');
  }

  if (status === 500) {
    if (errorText.toLowerCase().includes('send')) {
      return t('auth:emailVerification.resendFailed');
    }
  }

  return getGenericError(status, t, 'emailVerification');
}

function mapPhoneVerificationError(status: number, errorText: string, t: TFunction): string {
  if (status === 400) {
    if (errorText.toLowerCase().includes('invalid') || errorText.toLowerCase().includes('code')) {
      return t('auth:phoneVerification.invalidCode');
    }
    if (errorText.toLowerCase().includes('expired')) {
      return t('auth:phoneVerification.codeExpiredResend');
    }
  }

  if (status === 500) {
    return t('auth:phoneVerification.resendFailed');
  }

  return getGenericError(status, t, 'phoneVerification');
}

function getGenericError(
  status: number,
  t: TFunction,
  context: string
): string {
  // Map HTTP status codes to appropriate messages
  if (status >= 500) {
    return t(`auth:${context}.serverError`);
  }

  if (status >= 400 && status < 500) {
    // For client errors, try to return context-specific default
    const fallbackKeys = [
      `auth:${context}.invalidCredentials`,
      `auth:${context}.networkError`,
    ];

    for (const key of fallbackKeys) {
      if (t(key) !== key) {
        return t(key);
      }
    }
  }

  return t(`auth:${context}.serverError`);
}

/**
 * Helper to check if an error is a network error
 */
export const isNetworkError = (error: any): boolean => {
  return !error.response && error.request;
};

/**
 * Helper to check if an error is a server error (5xx)
 */
export const isServerError = (error: any): boolean => {
  return error.response?.status >= 500;
};

/**
 * Helper to get validation errors from 422 responses
 */
export const getValidationErrors = (error: any): Record<string, string> => {
  if (error.response?.status === 422 && error.response?.data?.errors) {
    const errors: Record<string, string> = {};
    Object.entries(error.response.data.errors).forEach(([key, value]) => {
      errors[key] = Array.isArray(value) ? value[0] : value as string;
    });
    return errors;
  }
  return {};
};
